import React from 'react';
import { useIntl } from 'react-intl';
import CustomerDetails from '@whitelabel/rentalcover-shared/components/CustomerDetails';
import { ICustomer } from '@whitelabel/rentalcover-shared/helpers/types';
import { getFullnameFromUser } from '@whitelabel/rentalcover-shared/helpers/auth';
import { ReactComponent as IconCustomer } from '@whitelabel/rentalcover-shared/media/images/icon-customer.svg';
import { StyledDropdown, StyledDropdownToggle } from '@whitelabel/rentalcover-shared/styled/StyledBaseDropdown';
import { LocaleTransformer } from '@whitelabel/rentalcover-shared/helpers/types/url';
import { StyledNavIconWrapper, StyledNavIcon } from '../NavItem/styledNavItem';
import { claimsLink, logoutLink, profileLink, protectionLink, offersLink } from '../../helpers/navLinks';
import { ReactComponent as IconBookings } from '../../media/icons/calendar-check.svg';
import { ReactComponent as IconClaims } from '../../media/icons/scroll-text.svg';
import { ReactComponent as IconOffers } from '../../media/icons/tag.svg';
import DropdownItem from './DropdownItem';
import { StyledDropdownButton, StyledDropdownItemDivider, StyledDropdownMenu } from './styledCustomerDropdown';

interface ICustomerDropdownProps {
  customer: ICustomer;
  localeTransformer?: LocaleTransformer;
  showOffers?: boolean;
}

const CustomerDropdown = ({ customer, localeTransformer, showOffers = true }: ICustomerDropdownProps): JSX.Element => {
  const intl = useIntl();
  const fullName = getFullnameFromUser(customer) || '';
  const compatibilityLocaleTransformer = localeTransformer || ((path: string) => `/${intl.locale}${path}`);

  return (
    <StyledDropdown>
      <StyledDropdownToggle
        // Override with existing button
        tag={StyledDropdownButton}
        buttonType="tertiary"
        data-test-id="CustomerDropdown-toggle"
      >
        <StyledNavIconWrapper>
          <StyledNavIcon as={IconCustomer} />
        </StyledNavIconWrapper>
        {fullName}
      </StyledDropdownToggle>

      <StyledDropdownMenu $rtl={intl.bidi} end={!intl.bidi} direction="down" data-test-id="CustomerDropdown-menu">
        <CustomerDetails customer={customer} />
        <StyledDropdownItemDivider />
        <DropdownItem
          {...protectionLink(intl)}
          icon={IconBookings}
          href={compatibilityLocaleTransformer(protectionLink(intl).path)}
          data-test-id="dropdown-item-protection"
        />
        <DropdownItem
          {...claimsLink(intl)}
          icon={IconClaims}
          href={compatibilityLocaleTransformer(claimsLink(intl).path)}
          data-test-id="dropdown-item-claims"
        />
        {showOffers && (
          <DropdownItem
            {...offersLink(intl)}
            icon={IconOffers}
            href={compatibilityLocaleTransformer(offersLink(intl).path)}
            data-test-id="dropdown-item-offers"
          />
        )}
        <DropdownItem
          {...profileLink(intl)}
          icon={IconCustomer}
          href={compatibilityLocaleTransformer(profileLink(intl).path)}
          data-test-id="dropdown-item-profile"
        />
        <StyledDropdownItemDivider />
        <DropdownItem
          {...logoutLink(intl)}
          href={compatibilityLocaleTransformer(logoutLink(intl).path)}
          data-test-id="dropdown-item-logout"
        />
      </StyledDropdownMenu>
    </StyledDropdown>
  );
};

export default CustomerDropdown;
